import * as React from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import {
  SortableSectionsContext,
  type SortableSectionsContextValue,
  type SectionDropLine,
} from "../../../../ui/components/CollapsibleSection/SortableSectionsContext";
import {
  SECTION_REORDER_CLICK_THRESHOLD_PX,
  SECTION_REORDER_ENTER_MS,
  applySectionReorder,
  resolveSectionInsertBefore,
} from "../../../../ui/components/CollapsibleSection/sectionReorder";
import * as s from "./SortableSections.style.css";

const CLICK_THRESHOLD_PX = SECTION_REORDER_CLICK_THRESHOLD_PX;
const ENTER_MS = SECTION_REORDER_ENTER_MS;
const DRAG_IDX_ATTR = "data-section-drag-idx";

type DragGhost = {
  clone: HTMLElement;
  w: number;
  h: number;
  sourceLeft: number;
};

/** Force the clone into the "handle revealed / title slid" look. */
function sanitizeClone(root: HTMLElement, isCompact: boolean): void {
  root.removeAttribute("id");
  root.removeAttribute("data-section-nav-id");
  root.removeAttribute("data-section-drag-idx");
  root.setAttribute("data-section-drag-ghost", "");
  root.querySelectorAll("[id]").forEach((el) => el.removeAttribute("id"));
  root.querySelectorAll("[aria-controls]").forEach((el) => el.removeAttribute("aria-controls"));

  const slidePx = isCompact ? 24 : 30;

  const handle = root.querySelector("[data-section-drag-handle]") as HTMLElement | null;
  if (handle) {
    handle.style.opacity = "1";
    handle.style.pointerEvents = "none";
    handle.style.transform = "translateY(-50%) scale(1)";
    handle.style.transition = "none";
  }

  const slide = root.querySelector("[data-section-title-slide]") as HTMLElement | null;
  if (slide) {
    slide.style.transform = `translateX(${slidePx}px)`;
    slide.style.transition = "none";
  }

  const subtitle = root.querySelector("[data-section-title-subtitle]") as HTMLElement | null;
  if (subtitle) {
    subtitle.style.transform = `translateX(${slidePx}px)`;
    subtitle.style.transition = "none";
  }
}

function getSectionId(child: React.ReactNode): string | null {
  if (!React.isValidElement(child)) return null;
  const id = (child.props as { id?: unknown }).id;
  return typeof id === "string" && id.length > 0 ? id : null;
}

function partitionChildren(
  children: React.ReactNode,
  sectionIdSet: Set<string>,
): { nonSection: React.ReactNode[]; byId: Map<string, React.ReactElement> } {
  const nonSection: React.ReactNode[] = [];
  const byId = new Map<string, React.ReactElement>();

  React.Children.forEach(children, (child) => {
    const id = getSectionId(child);
    if (id && sectionIdSet.has(id) && React.isValidElement(child)) {
      byId.set(id, child);
    } else if (child != null && child !== false) {
      nonSection.push(child);
    }
  });

  return { nonSection, byId };
}

export type SortableSectionsProps = {
  sectionIds: string[];
  onReorder: (orderedIds: string[]) => void;
  children: React.ReactNode;
  className?: string;
};

export function SortableSections({
  sectionIds,
  onReorder,
  children,
  className,
}: SortableSectionsProps) {
  const sectionIdsRef = React.useRef(sectionIds);
  sectionIdsRef.current = sectionIds;
  const onReorderRef = React.useRef(onReorder);
  onReorderRef.current = onReorder;

  const [dragSrcId, setDragSrcId] = React.useState<string | null>(null);
  const [dragSrcHover, setDragSrcHover] = React.useState(false);
  const [dropLine, setDropLine] = React.useState<SectionDropLine | null>(null);

  const [dragGhost, setDragGhost] = React.useState<DragGhost | null>(null);
  const [dragGhostPos, setDragGhostPos] = React.useState({ x: 0, y: 0 });
  const dragGrabOffset = React.useRef({ x: 0, y: 0 });
  const pointerStart = React.useRef<{ x: number; y: number } | null>(null);
  const didDragRef = React.useRef(false);
  const [pointerTracking, setPointerTracking] = React.useState(false);
  const pendingDragRef = React.useRef<{
    id: string;
    title: string;
    ghost: DragGhost;
  } | null>(null);
  const ghostHostRef = React.useRef<HTMLDivElement | null>(null);

  const [enteringIds, setEnteringIds] = React.useState(() => new Set<string>());

  React.useLayoutEffect(() => {
    const host = ghostHostRef.current;
    if (!host || !dragGhost) return;
    if (host.firstChild !== dragGhost.clone) {
      host.replaceChildren(dragGhost.clone);
    }
  }, [dragGhost]);

  React.useEffect(() => {
    if (!pointerTracking) return;

    const onPointerMove = (e: PointerEvent) => {
      const start = pointerStart.current;
      const pending = pendingDragRef.current;

      if (start && pending && !didDragRef.current) {
        const dist = Math.hypot(e.clientX - start.x, e.clientY - start.y);
        if (dist < CLICK_THRESHOLD_PX) return;

        didDragRef.current = true;
        document.documentElement.style.setProperty("cursor", "grabbing", "important");
        document.body.style.userSelect = "none";
        setDragSrcId(pending.id);
        setDragGhost(pending.ghost);
        setDragGhostPos({ x: e.clientX, y: e.clientY });
      }

      if (!didDragRef.current) return;

      setDragGhostPos({ x: e.clientX, y: e.clientY });

      const ids = sectionIdsRef.current;
      const src = pending?.id ? ids.indexOf(pending.id) : -1;
      const { insertBefore, line } = resolveSectionInsertBefore(
        e.clientX,
        e.clientY,
        ids,
        DRAG_IDX_ATTR,
      );

      // Over source slot (would be a no-op drop) → square indicator, no insertion line.
      if (src >= 0 && (insertBefore === src || insertBefore === src + 1)) {
        setDragSrcHover(true);
        setDropLine(null);
      } else {
        setDragSrcHover(false);
        setDropLine(line);
      }
    };

    const onPointerUp = (e: PointerEvent) => {
      document.documentElement.style.removeProperty("cursor");
      document.body.style.userSelect = "";

      const pending = pendingDragRef.current;
      const srcId = pending?.id ?? null;
      const moved = didDragRef.current;

      setPointerTracking(false);
      pendingDragRef.current = null;
      setDragSrcId(null);
      setDragSrcHover(false);
      setDropLine(null);
      setDragGhost(null);
      pointerStart.current = null;
      didDragRef.current = false;

      if (!moved || !srcId) return;

      const ids = [...sectionIdsRef.current];
      const src = ids.indexOf(srcId);
      if (src < 0) return;

      const { insertBefore } = resolveSectionInsertBefore(
        e.clientX,
        e.clientY,
        ids,
        DRAG_IDX_ATTR,
      );
      const next = applySectionReorder(ids, src, insertBefore);
      if (!next) return;

      const at = next.indexOf(srcId);
      onReorderRef.current(next);

      requestAnimationFrame(() => {
        const lo = Math.min(src, at);
        const hi = Math.max(src, at);
        const movedIds = next.slice(lo, hi + 1);
        setEnteringIds((p) => {
          const set = new Set(p);
          movedIds.forEach((id) => set.add(id));
          return set;
        });
        window.setTimeout(() => {
          setEnteringIds((p) => {
            const set = new Set(p);
            movedIds.forEach((id) => set.delete(id));
            return set;
          });
        }, ENTER_MS);
      });
    };

    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
    return () => {
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
    };
  }, [pointerTracking]);

  const onHandlePointerDown = React.useCallback(
    (sectionId: string, title: string, e: React.PointerEvent) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();

      const card = (e.currentTarget as HTMLElement).closest(
        "[data-section-nav-id]",
      ) as HTMLElement | null;
      if (!card) return;

      const rect = card.getBoundingClientRect();
      dragGrabOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      pointerStart.current = { x: e.clientX, y: e.clientY };
      didDragRef.current = false;

      // Clone while content is still fully visible (before dragSrcId hides it).
      const liveHandle = card.querySelector(
        "[data-section-drag-handle]",
      ) as HTMLElement | null;
      const isCompact = liveHandle != null && liveHandle.clientHeight <= 17;
      const clone = card.cloneNode(true) as HTMLElement;
      sanitizeClone(clone, isCompact);
      clone.style.margin = "0";
      clone.style.width = "100%";
      clone.style.height = "100%";

      pendingDragRef.current = {
        id: sectionId,
        title,
        ghost: {
          clone,
          w: Math.round(rect.width),
          h: Math.round(rect.height),
          sourceLeft: rect.left,
        },
      };
      setPointerTracking(true);
    },
    [],
  );

  const sectionIdSet = React.useMemo(() => new Set(sectionIds), [sectionIds]);
  const { nonSection, byId } = React.useMemo(
    () => partitionChildren(children, sectionIdSet),
    [children, sectionIdSet],
  );

  const orderedSections: React.ReactElement[] = [];
  for (const id of sectionIds) {
    const el = byId.get(id);
    if (el) orderedSections.push(el);
  }
  for (const [id, el] of byId) {
    if (!sectionIdSet.has(id)) orderedSections.push(el);
  }

  const ctx = React.useMemo<SortableSectionsContextValue>(
    () => ({
      enabled: true,
      sectionIds,
      dragSrcId,
      dragSrcHover,
      dropLine,
      enteringIds,
      onHandlePointerDown,
    }),
    [sectionIds, dragSrcId, dragSrcHover, dropLine, enteringIds, onHandlePointerDown],
  );

  const ghostLeft = dragGhost ? dragGhost.sourceLeft : 0;
  const ghostTop = dragGhost ? dragGhostPos.y - dragGrabOffset.current.y : 0;

  return (
    <SortableSectionsContext.Provider value={ctx}>
      <div className={clsx(s.list, className)}>
        {nonSection}
        {orderedSections.map((child) => {
          const id = getSectionId(child)!;
          return React.cloneElement(child, { key: id });
        })}
      </div>

      {dragGhost &&
        createPortal(
          <div
            ref={ghostHostRef}
            className={s.ghost}
            style={{
              left: ghostLeft,
              top: ghostTop,
              width: dragGhost.w,
              height: dragGhost.h,
              transform: "scale(1.03) rotate(1.5deg)",
              transformOrigin: `${dragGrabOffset.current.x}px ${dragGrabOffset.current.y}px`,
            }}
            aria-hidden
          />,
          document.body,
        )}
    </SortableSectionsContext.Provider>
  );
}
